import {
  useState,
  useCallback,
  Dispatch,
  SetStateAction,
  useEffect,
} from "react";
import {
  Checkbox,
  TextField,
  Stack,
  ICheckboxProps,
  ITextFieldProps,
  ComboBox,
  IconButton,
  MessageBarType,
  MessageBar,
} from "@fluentui/react";
import {
  createSystemTemplate,
  SystemTemplate,
  deleteSystemTemplate,
} from "../../api";
import { useId } from "@fluentui/react-hooks";
import { HelpCallout } from "../../components/HelpCallout";
import { useLogin, getToken } from "../../authConfig";
import { useMsal } from "@azure/msal-react";
import { useTranslation } from "react-i18next";

interface TemplateFormProps {
  idToken?: string;
  initialTemplates?: SystemTemplate[];
  onTemplateUpdate?: (templates: SystemTemplate[]) => void;
  useSemanticTemplate: boolean;
  setUseSemanticTemplate: Dispatch<SetStateAction<boolean>>;
  isRouterInitialized: boolean;
}

export const TemplateForm: React.FC<TemplateFormProps> = ({
  idToken,
  initialTemplates = [],
  onTemplateUpdate,
  useSemanticTemplate,
  setUseSemanticTemplate,
  isRouterInitialized,
}) => {
  const { t } = useTranslation();
  const client = useLogin ? useMsal().instance : undefined;

  // State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [templateName, setTemplateName] = useState<string>("");
  const [templateContent, setTemplateContent] = useState<string>("");
  const [systemMessage, setSystemMessage] = useState<string>(""); // New state for system message
  const [selectedTemplate, setSelectedTemplate] = useState<string>();
  const [templates, setTemplates] =
    useState<SystemTemplate[]>(initialTemplates);

  // Update local state when initialTemplates changes
  useEffect(() => {
    setTemplates(initialTemplates);
  }, [initialTemplates]);

  // IDs
  const semanticTemplateId = useId("semanticTemplateId");
  const semanticTemplateFieldId = useId("semanticTemplateFieldId");
  const templateNameId = useId("tempalteNameId");
  const templateNameFieldId = useId("templateNameFieldId");
  const templateContentId = useId("templateContentId");
  const templateContentFieldId = useId("templateContentFieldId");
  const systemMessageId = useId("systemMessageId");
  const systemMessageFieldId = useId("systemMessageFieldId");

  // Update local templates and notify parent
  const updateTemplates = useCallback(
    (newTemplates: SystemTemplate[]) => {
      setTemplates(newTemplates);
      onTemplateUpdate?.(newTemplates);
    },
    [onTemplateUpdate]
  );

  const onUseSemanticTemplateChange = useCallback(
    async (_ev?: React.FormEvent<HTMLElement>, checked?: boolean) => {
      if (checked) {
        if (
          !templateName.trim() ||
          !templateContent.trim() ||
          !systemMessage.trim()
        ) {
          console.error(
            "Template name, content, and system message are required"
          );
          setUseSemanticTemplate(false);
          return;
        }

        setIsLoading(true);
        try {
          const template: SystemTemplate = {
            name: templateName.trim(),
            template_content: templateContent.trim(),
            system_message: systemMessage.trim(),
          };

          const idToken = client ? await getToken(client) : undefined;
          const response = await createSystemTemplate(template, idToken);

          if (!response.ok) {
            throw new Error("Failed to create template");
          }

          // Add new template to local state
          const newTemplate = await response.json();
          updateTemplates([...templates, newTemplate]);
          setUseSemanticTemplate(true);
        } catch (error) {
          console.error("Error creating template:", error);
          setUseSemanticTemplate(false);
        } finally {
          setIsLoading(false);
        }
      } else {
        setUseSemanticTemplate(false);
      }
    },
    [
      templateName,
      templateContent,
      systemMessage,
      client,
      templates,
      updateTemplates,
      setUseSemanticTemplate,
    ]
  );

  const handleDeleteTemplate = async () => {
    if (!selectedTemplate) return;

    try {
      const idToken = client ? await getToken(client) : undefined;
      const response = await deleteSystemTemplate(selectedTemplate, idToken);
      if (response.ok) {
        // Update local state after successful deletion
        const updatedTemplates = templates.filter(
          (t) => t.id !== selectedTemplate && t.name !== selectedTemplate
        );
        updateTemplates(updatedTemplates);
        setSelectedTemplate(undefined);
        setTemplateName("");
        setTemplateContent("");
        setSystemMessage("");
        setUseSemanticTemplate(false);
      }
    } catch (error) {
      console.error("Error deleting template:", error);
    }
  };

  return (
    <Stack tokens={{ childrenGap: 10 }}>
      {!isRouterInitialized ? (
        <MessageBar messageBarType={MessageBarType.warning}>
          {t("helpTexts.enableSemanticRouter")}
        </MessageBar>
      ) : (
        <>
          <Checkbox
            id={semanticTemplateId}
            checked={useSemanticTemplate}
            label={t("labels.useSemanticTemplate")}
            onChange={onUseSemanticTemplateChange}
            disabled={
              isLoading || !templateName || !templateContent || !systemMessage
            }
            aria-labelledby={semanticTemplateId}
            onRenderLabel={(props: ICheckboxProps | undefined) => (
              <HelpCallout
                labelId={semanticTemplateId}
                fieldId={semanticTemplateFieldId}
                helpText={t("helpTexts.useSemanticTemplate")}
                label={props?.label}
              />
            )}
          />

          <TextField
            label={t("labels.templateName")}
            value={templateName}
            onChange={(_, newValue) => setTemplateName(newValue || "")}
            disabled={isLoading}
            required
          />

          <TextField
            label={t("labels.templateContent")}
            value={templateContent}
            onChange={(_, newValue) => setTemplateContent(newValue || "")}
            multiline
            rows={4}
            disabled={isLoading}
            required
          />

          <TextField
            id={systemMessageFieldId}
            label={t("labels.systemMessage")}
            value={systemMessage}
            onChange={(_, newValue) => setSystemMessage(newValue || "")}
            multiline
            rows={3}
            disabled={isLoading}
            required
            aria-labelledby={systemMessageId}
            onRenderLabel={(props: ITextFieldProps | undefined) => (
              <HelpCallout
                labelId={systemMessageId}
                fieldId={systemMessageFieldId}
                helpText={t("helpTexts.systemMessage")}
                label={props?.label}
              />
            )}
          />

          <Stack horizontal tokens={{ childrenGap: 5 }}>
            <Stack.Item grow>
              <ComboBox
                label={t("labels.existingTemplates")}
                options={templates.map((t) => ({
                  key: t.id || t.name,
                  text: t.name,
                }))}
                selectedKey={selectedTemplate}
                onChange={(_, option) => {
                  if (option) {
                    setSelectedTemplate(option.key as string);
                    const template = templates.find(
                      (t) => t.id === option.key || t.name === option.key
                    );
                    if (template) {
                      setTemplateName(template.name);
                      setTemplateContent(template.template_content);
                      setSystemMessage(template.system_message);
                    }
                  }
                }}
              />
            </Stack.Item>
            <Stack.Item align="end">
              <IconButton
                iconProps={{ iconName: "Delete" }}
                onClick={handleDeleteTemplate}
                disabled={!selectedTemplate}
                styles={{ root: { marginTop: 22 } }}
                ariaLabel={t("labels.deleteTemplate")}
              />
            </Stack.Item>
          </Stack>
        </>
      )}
    </Stack>
  );
};
